/**
 * `<Toggle>` — minimal switch primitive (track + thumb), role="switch".
 *
 * Built for the SA Demo Detail "Environment Security" card (MFA policy is a
 * boolean policy → a switch communicates that better than a verb-button).
 * Token-correct, no raw hex: off-track = surface-subtle + border ring,
 * on-track = accent-primary, thumb = text-on-brand.
 *
 * Pending state: dims + shows a spinner ring on the thumb and blocks onChange.
 * Accessible name MUST be supplied by the caller via aria-label.
 */
import { type ButtonHTMLAttributes } from "react";

type Props = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "type" | "role" | "aria-checked"
> & {
  checked: boolean;
  onChange: () => void;
  /** When true: dims, shows a spinning thumb, and blocks onChange. */
  pending?: boolean;
};

export function Toggle({
  checked,
  onChange,
  pending = false,
  disabled,
  className = "",
  ...props
}: Props): JSX.Element {
  const isDisabled = disabled || pending;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={isDisabled}
      onClick={() => {
        if (isDisabled) return;
        onChange();
      }}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked
          ? "bg-accent-primary"
          : "bg-surface-subtle ring-1 ring-inset ring-border-default"
      } ${className}`}
      {...props}
    >
      <span
        aria-hidden="true"
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full bg-text-on-brand shadow-sm transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-0.5"
        }`}
      >
        {pending ? (
          <span className="h-2.5 w-2.5 animate-spin rounded-full border border-accent-primary border-t-transparent" />
        ) : null}
      </span>
    </button>
  );
}
